import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router';
import { X, Diff, Sigma, Lock } from 'lucide-react';
import { levels, type LevelDef } from '@/config/levels.config';
import { groups, modes, type ModeDef } from '@/config/modes.config';
import { LINE_COUNTS, aNumbersFor, comboCount } from '@/engine/combos';
import { ComboCards } from '@/ui/ComboCards';
import { useSettings } from '@/store/settings';
import { useAccess } from '@/services/entitlement';
import { HomeLayout } from './HomeLayout';

const ICON: Record<LevelDef['icon'], typeof X> = { x: X, pm: Diff, sigma: Sigma };

/** Level cards for one mode, inside the same frame as the home screen. */
export function ModeScreen() {
  const { t } = useTranslation();
  const { modeId } = useParams();
  const mode = modes.find((m) => m.id === modeId);
  if (!mode) return <Navigate to="/" replace />;

  return (
    <HomeLayout
      section={`${mode.group ? `${t(`group.${mode.group}.name`)} · ` : ''}${t(`mode.${mode.id}.name`)} · ${t('home.levels')}`}
      backTo={mode.group ? `/g/${mode.group}` : '/'}
    >
      <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-6">
        {levels.map((level, i) => (
          <LevelCard key={level.id} mode={mode} level={level} index={i} />
        ))}
      </div>
    </HomeLayout>
  );
}

function LevelCard({ mode, level, index }: { mode: ModeDef; level: LevelDef; index: number }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const access = useAccess(`${mode.id}:${level.id}`);
  const custom = groups.find((g) => g.id === mode.group)?.blockers === 'custom';
  const sub = useSettings((s) => s.subIndex(mode.id, level.id));
  const setSub = useSettings((s) => s.setSub);
  const Icon = ICON[level.icon];
  const go = (): void => {
    if (access.allowed) navigate(`/play/${mode.id}/${level.id}`);
  };
  const formula = Array.from({ length: level.terms }, (_, i) => (
    <span key={i}>
      {i > 0 && <span className="text-fg-muted"> ± </span>}
      <span className="va">A</span>
      <span className="text-fg-muted">×</span>
      <span className="vb">B</span>
    </span>
  ));

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={t(`level.${level.id}.name`)}
      aria-disabled={!access.allowed || undefined}
      onClick={go}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          go();
        }
      }}
      className="enter relative flex min-h-[220px] cursor-pointer flex-col gap-3.5 overflow-hidden rounded-[20px] border-2 border-accent bg-surface p-7 pb-6 text-left transition-transform hover:-translate-y-[3px] aria-disabled:cursor-not-allowed aria-disabled:opacity-60"
      style={{ boxShadow: 'var(--glow)', animationDelay: `${index * 60}ms` }}
    >
      {!access.allowed && (
        <span className="absolute inset-0 z-10 grid place-items-center bg-[color-mix(in_oklch,var(--bg)_70%,transparent)] text-[12px] font-bold tracking-[0.16em] text-fg uppercase">
          <span className="inline-flex items-center gap-2">
            <Lock className="size-4" /> {t('common.locked')}
          </span>
        </span>
      )}
      <div className="flex items-center gap-3.5">
        <Icon className="size-[30px] text-accent" strokeWidth={2.4} />
        <span className="font-display text-[26px] leading-[1.1] font-extrabold uppercase italic text-accent">
          {t(`level.${level.id}.name`)}
        </span>
      </div>
      <div className="text-[15px] font-bold tracking-[0.08em]">{formula}</div>
      <p className="m-0 max-w-[40ch] text-[13.5px] leading-[1.6]">
        {t(`level.${level.id}.desc`, { max: mode.bSumMax })}
      </p>
      {custom && <BlockerLines mode={mode} level={level} />}
      <div
        className={`${custom ? '' : 'mt-auto '}flex flex-wrap items-center justify-between gap-2.5 border-t border-line pt-4`}
      >
        <span className="label">{t('home.difficulty')}</span>
        <span
          className="inline-flex gap-1 rounded-full border border-line-strong bg-bg p-[3px]"
          role="radiogroup"
          aria-label={t('home.difficulty')}
        >
          {mode.caps.map((cap, i) => (
            <button
              key={cap}
              type="button"
              role="radio"
              aria-checked={sub === i}
              onClick={(e) => {
                e.stopPropagation();
                setSub(mode.id, level.id, i);
              }}
              className="h-7 rounded-full px-2.5 text-[11px] font-bold tracking-[0.06em] text-fg-muted hover:text-fg aria-checked:bg-accent aria-checked:text-bg"
            >
              {cap}
            </button>
          ))}
        </span>
      </div>
      <Icon
        className="absolute top-[22px] right-[22px] size-11 text-accent opacity-20"
        strokeWidth={2.4}
        aria-hidden
      />
    </div>
  );
}

/** Card Blockers, per level: rows and columns our cards block, with the grid and the A values updating live. */
function BlockerLines({ mode, level }: { mode: ModeDef; level: LevelDef }) {
  const { t } = useTranslation();
  const blockers = useSettings((s) => s.blockersFor(mode.id, level.id));
  const setCardBlockers = useSettings((s) => s.setCardBlockers);
  // the preview paints one line for a random count
  const rows = Array.from({ length: blockers.rows === 'random' ? 1 : blockers.rows }, (_, i) => i);
  const cols = Array.from(
    { length: blockers.cols === 'random' ? 1 : blockers.cols },
    (_, i) => 3 - i,
  );
  const picker = (key: 'rows' | 'cols'): React.ReactElement => (
    <div className="flex items-center justify-between gap-2.5">
      <span className="label">{t(`home.${key === 'rows' ? 'blockRows' : 'blockCols'}`)}</span>
      <span
        className="inline-flex gap-1 rounded-full border border-line-strong bg-bg p-[3px]"
        role="radiogroup"
        aria-label={t(`home.${key === 'rows' ? 'blockRows' : 'blockCols'}`)}
      >
        {LINE_COUNTS.map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={blockers[key] === n}
            onClick={(e) => {
              e.stopPropagation();
              setCardBlockers(mode.id, level.id, { [key]: n });
            }}
            className="h-7 min-w-7 rounded-full px-2.5 text-[11px] font-bold tracking-[0.06em] text-fg-muted hover:text-fg aria-checked:bg-accent aria-checked:text-bg"
          >
            {n === 'random' ? 'R' : n}
          </button>
        ))}
      </span>
    </div>
  );
  return (
    <div className="mt-auto flex items-center gap-4 border-t border-line pt-4">
      <div
        className="terms shrink-0"
        style={{ '--cell': '5px' } as React.CSSProperties}
        aria-hidden
      >
        <ComboCards
          a={comboCount('both', rows, cols)}
          side="both"
          blockedRows={rows}
          blockedCols={cols}
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        {picker('rows')}
        {picker('cols')}
        <span className="label">
          A: <span className="va">{aNumbersFor(blockers).join(' · ')}</span>
        </span>
      </div>
    </div>
  );
}
